import { useMemo } from 'react';
import type {
    BusinessUnitStore,
    Province,
} from '../../../../stores/business-unit';
import { provinceTranslations } from '../utils/province-translations';

interface Props<T extends string | number> {
    businessUnitStore: BusinessUnitStore;
    getProvinceValue: (province: Province) => T;
    getProvinceLabel?: (province: Province) => string;
}

interface BusinessUnitNode {
    title: string;
    value: string | typeof SELECT_ALL_VALUE;
    children?: BusinessUnitNode[];
    checkable?: boolean;
}

export const SELECT_ALL_VALUE = 'SELECT_ALL';

const defaultGetProvinceLabel = (province: Province) => {
    const provinceCode = province.name.toUpperCase();
    return (
        provinceTranslations[
            provinceCode as keyof typeof provinceTranslations
        ] || province.name
    );
};

export const useConstructBusinessUnitTreeData = <T extends string | number>({
    businessUnitStore,
    getProvinceValue,
    getProvinceLabel = defaultGetProvinceLabel,
}: Props<T>) => {
    const allProvinceValues: T[] = useMemo(() => {
        if (!businessUnitStore.businessUnits) return [];

        return businessUnitStore.businessUnits.flatMap((bu) =>
            bu.provinces.map(getProvinceValue)
        );
    }, [businessUnitStore.businessUnits, getProvinceValue]);

    const businessUnitToProvincesMap = useMemo(() => {
        if (!businessUnitStore.businessUnits) return new Map<number, T[]>();

        const map = new Map<number, T[]>();
        businessUnitStore.businessUnits.forEach((bu) => {
            map.set(bu.id, bu.provinces.map(getProvinceValue));
        });
        return map;
    }, [businessUnitStore.businessUnits, getProvinceValue]);

    const businessUnitNodes: BusinessUnitNode[] = useMemo(
        () =>
            businessUnitStore.businessUnits?.flatMap((businessUnit) => {
                if (businessUnit.provinces.length === 1) {
                    const province = businessUnit.provinces[0];
                    const provinceValue = getProvinceValue(province);
                    return [
                        {
                            title: getProvinceLabel(province),
                            value: `province-${String(provinceValue)}`,
                        },
                    ];
                }

                return [
                    {
                        title: businessUnit.name,
                        value: `bu-${businessUnit.id}`,
                        children: businessUnit.provinces.map((province) => {
                            const provinceValue = getProvinceValue(province);
                            return {
                                title: getProvinceLabel(province),
                                value: `province-${String(provinceValue)}`,
                            };
                        }),
                    },
                ];
            }) || [],
        [businessUnitStore.businessUnits, getProvinceLabel, getProvinceValue]
    );

    const treeData: BusinessUnitNode[] = useMemo(() => {
        const selectAllNode = {
            title: 'Select all',
            value: SELECT_ALL_VALUE as typeof SELECT_ALL_VALUE,
            checkable: true,
        };
        return [selectAllNode, ...businessUnitNodes];
    }, [businessUnitNodes]);

    return {
        treeData,
        allProvinceValues,
        businessUnitToProvincesMap,
    };
};
